import { useEffect, useRef, useState } from 'react'
import type { Locale } from '../data/schema'
import { Icon } from './Icon'

interface ShareMenuProps {
  locale: Locale
  busy: boolean
  onCopyLink: () => void
  onShareImage: () => Promise<void>
}

export function ShareMenu({ locale, busy, onCopyLink, onShareImage }: ShareMenuProps) {
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const firstActionRef = useRef<HTMLButtonElement>(null)
  const labels = locale === 'en' ? {
    trigger: 'Share', options: 'Share this view', eyebrow: 'Share the myth atlas',
    link: 'Copy interactive link', linkHint: 'Let others continue exploring this exact view',
    image: 'Share graph image', imageHint: 'Create a branded 1200 × 1200 PNG', preparing: 'Preparing graph image…',
  } : {
    trigger: '分享', options: '分享目前畫面', eyebrow: '分享神話地圖',
    link: '複製互動連結', linkHint: '其他人開啟後會看到相同位置和內容',
    image: '分享關係圖圖片', imageHint: '製作 1200 × 1200 PNG 圖片', preparing: '正在製作關係圖…',
  }

  useEffect(() => {
    if (!open) return
    firstActionRef.current?.focus()
    const handlePointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false)
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      setOpen(false)
      triggerRef.current?.focus()
    }
    document.addEventListener('pointerdown', handlePointerDown)
    window.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown)
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [open])

  const runAction = (action: () => void) => {
    action()
    setOpen(false)
  }

  const shareImage = async () => {
    await onShareImage()
    setOpen(false)
    triggerRef.current?.focus()
  }

  return <div className="share-control" ref={rootRef}>
    <button
      ref={triggerRef}
      className={open ? 'active' : ''}
      onClick={() => setOpen((value) => !value)}
      aria-label={labels.trigger}
      aria-expanded={open}
      aria-haspopup="dialog"
    ><Icon name="share" /><span className="wide-label">{labels.trigger}</span></button>
    {open && <section className="share-menu" role="dialog" aria-label={labels.options} data-testid="share-menu">
      <span className="share-menu-eyebrow">{labels.eyebrow}</span>
      <button ref={firstActionRef} type="button" onClick={() => runAction(onCopyLink)}>
        <span className="share-menu-icon"><Icon name="link" /></span>
        <span><strong>{labels.link}</strong><small>{labels.linkHint}</small></span>
      </button>
      <button type="button" disabled={busy} onClick={shareImage}>
        <span className="share-menu-icon"><Icon name="image" /></span>
        <span><strong>{busy ? labels.preparing : labels.image}</strong><small>{labels.imageHint}</small></span>
      </button>
    </section>}
  </div>
}
